import React, { useState, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import useFormValidation from 'hooks/useFormValidation';

import { adminUserApi } from 'api';
import {
  ADMIN_USER_TYPE,
  ADMIN_USER_ACTIVE_TYPE,
  ADMIN_CREATE_USER_VALIDATION_SCHEMA,
} from 'constants/admin/user';

const initialValues = {
  username: '',
  email: '',
  phone: '',
  is_admin: '',
  is_active: '',
};

const CreateUserModal = ({ show, onHide, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  } = useFormValidation(initialValues, ADMIN_CREATE_USER_VALIDATION_SCHEMA);

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await adminUserApi.create({
        username: values.username,
        email: values.email,
        phone: values.phone,
        is_admin: values.is_admin === 'true',
        is_active: values.is_active === 'true',
      });

      toast.success('Add user successfully');
      closeModal();
      onSuccess();
    } catch (error) {
      toast.error('Failed to add user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = useCallback(() => {
    resetForm();
    onHide();
  }, [resetForm, onHide]);

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>Add User</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Name<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              value={values.username}
              onChange={e => handleChange('username', e.target.value)}
              placeholder="Enter user name..."
              onBlur={() => handleBlur('username')}
              isInvalid={touched.username && errors.username}
            />
            <Form.Control.Feedback type="invalid">
              {errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Email<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              type="email"
              value={values.email}
              onChange={e => handleChange('email', e.target.value)}
              placeholder="Enter Email..."
              onBlur={() => handleBlur('email')}
              isInvalid={touched.email && errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              as="input"
              value={values.phone}
              onChange={e => handleChange('phone', e.target.value)}
              placeholder="Enter phone number..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              User Type<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.is_admin}
              onChange={e => handleChange('is_admin', e.target.value)}
              onBlur={() => handleBlur('is_admin')}
              isInvalid={touched.is_admin && errors.is_admin}
            >
              <option value="">Select User Type...</option>
              {ADMIN_USER_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.is_admin}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Status<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.is_active}
              onChange={e => handleChange('is_active', e.target.value)}
              onBlur={() => handleBlur('is_active')}
              isInvalid={touched.is_active && errors.is_active}
            >
              <option value="">Select Status...</option>
              {ADMIN_USER_ACTIVE_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.is_active}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={resetForm}>
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateUser}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateUserModal;
